import {isDemoMode} from '../demo/demoState';

export const MANAGER_ROLES = ['system:admin', 'restaurantStaff:admin', 'restaurantStaff:manager', 'sales:admin', 'sales:manager'];
// Known explicit service roles. Detection is NOT limited to this list — see
// `isServiceRole`, which matches ANY role starting with "service" so future
// service roles (serviceAdmin, serviceAdvisor, serviceExternal, serviceViewer…)
// unlock the Service app without code changes.
export const SERVICE_ROLES = ['serviceTechnician', 'serviceTechnicians', 'serviceAdmin', 'serviceAdvisor', 'serviceExternal', 'serviceViewer'];
// Roles that unlock the "Belépés mint" partner switch (act as any restaurant).
export const PARTNER_SWITCH_ROLES = ['system:admin', 'sales:admin'];
export const STOCK_ROLES = [
    'stock:manager',
    'inventoryManager',
    'delivery:manager',
    'delivery:driver',
    'external:UPS',
    'external:DHL',
    'external:FedEx',
    'external:iLogistic',
];

/**
 * Roles that unlock the Driver / courier (Sofor) app. Managers and admins may
 * switch into it too (see `getSwitchableAppModesForUser`) but it is never their
 * default mode.
 */
export const DRIVER_ROLES = ['delivery:driver', 'delivery:manager', 'restaurantStaff:delivery'];

/**
 * Every role value attached to the session, from all the shapes the user
 * document can carry them in.
 *
 * `defaultRoles` and `effectiveRoles` are collected too, and that is deliberate:
 * the partner switch ("Belépés mint", `ManagementSettingsScreen.handleLoginAsRestaurant`)
 * REPLACES `user.roles` with `['restaurantStaff:admin']` and parks the operator's
 * own roles in `defaultRoles`. Without them a `system:admin` impersonating a
 * partner silently lost every module the super-admin unlocks — the Sales
 * (Értékesítő) app included — and the switcher shrank to Management + POS.
 * `managementAccess.collectRoles`, `WhatsNewModal` and the partner-switch gate in
 * `ManagementSettingsScreen` already read all three lists; this keeps the module
 * entitlements consistent with them.
 *
 * @param {object} user  The authenticated (or impersonating) user document.
 * @param {object} [place]  The active restaurant place (place-scoped roles).
 * @returns {string[]} Unique role values as strings.
 */
export const collectUserRoles = (user, place) => {
    const roles = new Set();
    const add = (value) => {
        if (!value) return;
        if (Array.isArray(value)) {
            value.forEach(add);
            return;
        }
        roles.add(String(value));
    };

    add(user?.roles);
    add(user?.userRoles);
    add(user?.role);
    add(user?.defaultRoles);
    add(user?.effectiveRoles);
    add(place?.roles);
    add(place?.role);

    const restaurants = Array.isArray(user?.restaurants) ? user.restaurants : [];
    restaurants.forEach((restaurant) => {
        add(restaurant?.roles);
        add(restaurant?.role);
    });

    return Array.from(roles);
};

export const hasAnyRole = (user, place, allowedRoles = []) => {
    if (!allowedRoles.length) return false;
    const roles = collectUserRoles(user, place);
    return roles.some((role) => allowedRoles.includes(role));
};

// Any administrator-style role. Matches system:admin, anything ending in
// ":admin", and any role whose name contains "admin" (e.g. serviceAdmin).
// Case-insensitive so future admin roles unlock Management without code changes.
export const isAdminRole = (role) => {
    const value = String(role || '').trim().toLowerCase();
    if (!value) return false;
    return value.includes('admin');
};

export const hasAdminRole = (user, place) => collectUserRoles(user, place).some(isAdminRole);

// Users who may use the "Belépés mint" partner switch (system:admin / sales:admin).
export const canSwitchPartnerContext = (user, place) => hasAnyRole(user, place, PARTNER_SWITCH_ROLES);

// Any service-style role unlocks the Service (Szerviz) app: serviceTechnician,
// serviceAdmin, serviceAdvisor, serviceExternal, serviceViewer, service:* …
// Matching is case-insensitive and prefix-based so new service roles work
// without touching this file.
export const isServiceRole = (role) => {
    const value = String(role || '').trim().toLowerCase();
    if (!value) return false;
    return value.startsWith('service');
};

export const hasServiceRole = (user, place) => collectUserRoles(user, place).some(isServiceRole);

// Any sales-style role unlocks the Sales (Értékesítő / CRM field-sales) app:
// sales, sales:rep, sales:manager, sales:admin … Matching mirrors `isServiceRole`
// — case-insensitive and PREFIX-based — so new sales roles work without touching
// this file. This is ADDITIVE: a `sales:*` user keeps the POS (`restaurant`)
// entitlement `isSalesOrStaffRole` already grants and GAINS the `sales` mode.
export const isSalesRole = (role) => {
    const value = String(role || '').trim().toLowerCase();
    if (!value) return false;
    return value.startsWith('sales');
};

export const hasSalesRole = (user, place) => collectUserRoles(user, place).some(isSalesRole);

// Operational sales / restaurant / trade / erp role (explicitly NOT an admin role).
// These users land on the Sales (Értékesítő) app by default.
export const isSalesOrStaffRole = (role) => {
    const value = String(role || '');
    if (!value || isAdminRole(value)) return false;
    return value === 'sales'
        || value === 'restaurantStaff'
        || value === 'restaurant'
        || value === 'trade'
        || value === 'erp'
        || value.startsWith('sales:')
        || value.startsWith('restaurantStaff:')
        || value.startsWith('restaurant:')
        || value.startsWith('trade:')
        || value.startsWith('erp:');
};

export const hasSalesOrStaffRole = (user, place) => collectUserRoles(user, place).some(isSalesOrStaffRole);

/**
 * Names of the platform's OWN head-office store(s). Membership of one of these
 * in `user.restaurants[]` marks an internal Happy Solutions colleague, and those
 * users must get the whole product (Management + every switchable module) even
 * when their role list alone would only unlock one app — an internal employee is
 * onboarded onto the HQ store long before anybody remembers to hand them
 * `system:admin`, and until then the module switcher silently collapsed to the
 * single module their operational role implied.
 *
 * The values MIRROR the backend's own operator-tenant check — the set in
 * `HappyPOS-backend/src/api/services/leadConversion.service.js`
 * (`HAPPY_SOLUTIONS_MAIN_RESTAURANT_NAMES`), which is what decides that this is
 * the MAIN tenant. Keep the two lists in step so the app and the API never
 * disagree about which store is the head office; there is deliberately no
 * backend flag to read instead (see `isHqStore`).
 *
 * Matching is normalized (trimmed, collapsed whitespace, lower-cased) so a
 * differently-typed store name still resolves. Add new head-office store names
 * here — this is the ONLY place the HQ store is spelled out client-side.
 *
 * @type {string[]}
 */
export const HQ_STORE_NAMES = [
    'happy solutions (main)',
    'happy solutions',
];

/** Trim + collapse inner whitespace + lower-case, so " Happy  Solutions (Main) " matches. */
const normalizeStoreName = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Whether a store/place document is the platform's head-office store.
 *
 * The Restaurant model carries NO head-office boolean today (the backend
 * identifies the operator tenant by name too), so the name list is the working
 * rule. A flag is still honoured first (`isMain` / `isHeadquarter` /
 * `isHeadOffice`) so the day the store gets a real marker this stops depending
 * on its display name without another release. `user.restaurants` can also carry
 * bare id strings depending on the payload — those are simply not HQ.
 *
 * @param {object|string} store  A restaurant/store or place document.
 * @returns {boolean} Whether this is the Happy Solutions head-office store.
 */
export const isHqStore = (store) => {
    if (!store || typeof store !== 'object') {
        return false;
    }

    if (store.isMain === true || store.isHeadquarter === true || store.isHeadOffice === true) {
        return true;
    }

    return HQ_STORE_NAMES.includes(normalizeStoreName(store.name || store.restaurantName));
};

/**
 * Whether the session belongs to a user who is a member of the head-office store
 * — through `user.restaurants[]` (the login/`/me` payload), the single
 * `user.restaurant`, or the active place (and the restaurant it hangs off).
 *
 * Read by {@link getSwitchableAppModesForUser}, {@link canUseManagementMode} and
 * {@link getDefaultOperationalModeForUser}, which is why it also has to survive
 * the partner switch: `handleLoginAsRestaurant` swaps the impersonated store in,
 * so an operator acting as a partner is judged on THAT store — the same way
 * `collectUserRoles` still finds their own roles in `defaultRoles`.
 *
 * @param {object} user  The authenticated (or impersonating) user document.
 * @param {object} [place]  The active restaurant place.
 * @returns {boolean} Whether the user belongs to the Happy Solutions HQ store.
 */
export const hasHqStoreMembership = (user, place) => {
    const candidates = [];

    if (Array.isArray(user?.restaurants)) {
        candidates.push(...user.restaurants);
    }

    candidates.push(user?.restaurant, place, place?.restaurant);

    return candidates.some(isHqStore);
};

export const canUseManagementMode = (user, place) => hasAnyRole(user, place, MANAGER_ROLES)
    || hasAdminRole(user, place)
    || hasHqStoreMembership(user, place);

export const canUseServiceMode = (user, place) => hasServiceRole(user, place);

// Legacy name, unchanged semantics: the POS (`restaurant`) till, which used to be
// called "Sales". Kept as-is for backward compatibility — see
// `canUseFieldSalesMode` for the NEW `sales` (CRM field-sales) app mode.
export const canUseSalesMode = (user, place) => hasSalesOrStaffRole(user, place);

// The NEW `sales` app mode (CRM-driven field sales): any `sales:*` role.
export const canUseFieldSalesMode = (user, place) => hasSalesRole(user, place);

/**
 * Modes the user is entitled to use/switch to. This MUST stay identical to
 * {@link getSwitchableAppModesForUser} (it delegates to it): `App.jsx`
 * `loadRuntimeState` uses this set as `allowedModes` to decide whether to keep
 * the persisted `userAppMode` after a restart. If it were narrower than the set
 * the in-Settings selector offers, a chosen mode would be silently rejected on
 * reload and reverted to the role default — e.g. a `system:admin` picking Sales
 * but always landing back in Management. Keeping them the same guarantees every
 * mode the selector shows actually sticks. Delegating also means admins
 * (`system:admin` → all modules; `*:admin` → POS) are honoured consistently.
 */
export const getAvailableAppModesForUser = (user, place) => getSwitchableAppModesForUser(user, place);

/** The platform super-admin role, lower-case (role values are normalized before comparing). */
export const SYSTEM_ADMIN_ROLE = 'system:admin';

/**
 * `system:admin` is the super-admin role that unlocks EVERY module for switching
 * (Management, POS, Sales, Service, Warehouse).
 *
 * The comparison is normalized (trimmed + lower-cased) instead of a strict
 * `includes('system:admin')` so a differently-cased or padded role value from any
 * of the payload shapes still unlocks everything, and it reads through
 * {@link collectUserRoles}, so the role also counts when it arrives via
 * `defaultRoles` (partner switch) or `effectiveRoles`.
 *
 * @param {object} user  The authenticated user document.
 * @param {object} [place]  The active restaurant place (for place-scoped roles).
 * @returns {boolean} Whether the session belongs to the platform super-admin.
 */
export const isSystemAdmin = (user, place) => collectUserRoles(user, place)
    .some((role) => String(role || '').trim().toLowerCase() === SYSTEM_ADMIN_ROLE);

// Canonical display order for the in-app module switcher:
//   Management → POS (restaurant) → Sales (Értékesítő) → Service (Szerviz) → Warehouse (Raktáros).
export const SWITCHABLE_MODE_ORDER = ['manager', 'restaurant', 'sales', 'service', 'stock', 'driver'];

// Admin/manager roles that, on the sales side, also unlock the POS module.
const SALES_ADMIN_ROLES = ['restaurantStaff:admin', 'restaurantStaff:manager', 'sales:admin', 'sales:manager'];

/**
 * Modules the user is allowed to switch between from Settings, honouring role
 * entitlements. Each operational module is offered to users who hold one of its
 * roles; Management is offered to every admin/manager; `system:admin` unlocks all.
 *
 * Examples (product spec):
 *   - system:admin                         → Management, POS, Sales, Service, Warehouse
 *   - member of the HQ store               → Management, POS, Sales, Service, Warehouse
 *   - restaurantStaff:admin / :manager     → Management, POS
 *   - sales:* (any)                        → POS **and** Sales (+ Management for sales:admin/:manager)
 *   - service role + admin (e.g. serviceAdmin) → Management, Service
 *   - stock/logistics role + admin         → Management, Warehouse
 *   - a single operational role            → just that one module (no switch UI)
 *
 * @param {object} user  The authenticated user document.
 * @param {object} [place]  The active restaurant place (for place-scoped roles).
 * @returns {Array<'manager'|'restaurant'|'sales'|'service'|'stock'>} Modes in SWITCHABLE_MODE_ORDER.
 */
export const getSwitchableAppModesForUser = (user, place) => {
    // Demo (Bemutató) mode unlocks EVERY module so a presenter can walk a prospect
    // through the whole product regardless of the operator's real entitlements —
    // exactly like a system:admin. Session-only, so it reverts on cold start.
    //
    // Membership of the Happy Solutions head-office store unlocks everything too:
    // an internal colleague is put on the HQ store on day one but typically only
    // carries one operational role, which used to collapse the switcher to that
    // single app and hide Management entirely. See `hasHqStoreMembership`.
    if (isDemoMode() || isSystemAdmin(user, place) || hasHqStoreMembership(user, place)) {
        return [...SWITCHABLE_MODE_ORDER];
    }

    const entitlement = {
        manager: canUseManagementMode(user, place),
        restaurant: hasSalesOrStaffRole(user, place) || hasAnyRole(user, place, SALES_ADMIN_ROLES),
        sales: hasSalesRole(user, place),
        service: hasServiceRole(user, place),
        stock: hasAnyRole(user, place, STOCK_ROLES),
        driver: hasAnyRole(user, place, DRIVER_ROLES) || canUseManagementMode(user, place),
    };

    return SWITCHABLE_MODE_ORDER.filter((mode) => entitlement[mode]);
};

/** The only role allowed to run a demo: the super-admin. */
export const DEMO_MODE_ROLE = SYSTEM_ADMIN_ROLE;

/**
 * Whether the user may reveal & toggle Demo (Bemutató) mode from the app chrome.
 * Restricted to `system:admin` alone (mirrors the admin panel's
 * `src/demo/demoAccess.js`; `sales:*` no longer qualifies). Demo mode unlocks
 * EVERY module and replaces the visible data with synthetic records, so an
 * ordinary operator must never flip it and mistake sample data for their own.
 * `defaultRoles` counts too, so an admin impersonating a partner keeps the
 * toggle, and anyone already IN demo mode keeps it as well — otherwise they
 * could not switch it back off.
 *
 * @param {object} user  The authenticated user document.
 * @param {object} [place]  The active restaurant place (for place-scoped roles).
 * @returns {boolean} whether the demo toggle should be shown to this user.
 */
export const canUseDemoMode = (user, place) => {
    if (isDemoMode()) {
        return true;
    }

    // `collectUserRoles` already folds in `defaultRoles`/`effectiveRoles`, so the
    // impersonation case is covered by the shared super-admin check.
    return isSystemAdmin(user, place);
};

// Role-based DEFAULT module the user should land on:
//   - any admin role (system:admin, *:admin, serviceAdmin, …) -> Management app
//   - member of the Happy Solutions HQ store                  -> Management app
//   - serviceTechnician         -> Service app
//   - sales:* (non-admin)       -> Sales app (CRM field sales — a pure rep must
//                                  NOT land in the POS till)
//   - restaurantStaff           -> POS app
//   - stock / logistics roles   -> Logistics app
// Admin roles win: any user holding an admin-style role lands on the Management
// app by default. This default is ONLY applied when the user has NOT already
// picked & persisted another module — the persisted `userAppMode` always wins
// over this default (see App.jsx loadRuntimeState and RestaurantPlaceChooser).
export const getDefaultOperationalModeForUser = (user, place) => {
    if (hasAdminRole(user, place) || hasHqStoreMembership(user, place)) return 'manager';
    if (canUseServiceMode(user, place)) return 'service';
    if (hasSalesRole(user, place)) return 'sales';
    if (hasAnyRole(user, place, DRIVER_ROLES)) return 'driver';
    if (hasSalesOrStaffRole(user, place)) return 'restaurant';
    if (hasAnyRole(user, place, STOCK_ROLES)) return 'stock';
    if (canUseManagementMode(user, place)) return 'manager';
    return 'restaurant';
};

export const getAppModeForUser = (user, place) => {
    return getDefaultOperationalModeForUser(user, place);
};

export const isAllPlacesScope = (place) => Boolean(place?.managerAllPlaces || place?._id === '__all__');
